import { todayLocal } from '@/lib/dates'
import { useState } from 'react'
import { ClipboardList, Info, Plus } from 'lucide-react'
import {
  DateValue, EmptyState, ErrorState, FormDialog, PageHeader, StatusBadge,
} from '@/components/common'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { useBreadcrumb } from '@/hooks/useBreadcrumb'
import { useLookup } from '@/hooks/useLookup'
import { toApiProblem } from '@/lib/api/client'
import {
  useAssetCategories, useAssetMutations, useStocktakeLines, useStocktakes,
} from '../api'
import { ASSET_STATUS_LABELS, COUNT_OUTCOME_LABELS } from '../types'
import type { AssetStatus, CountOutcome } from '../types'
import { Select } from '@/components/ui/select'
import { DatePicker } from '@/components/ui/date-picker'

const OUTCOME_TONE: Record<CountOutcome, 'neutral' | 'info' | 'success' | 'warning' | 'danger'> = {
  NOT_COUNTED: 'neutral',
  FOUND: 'success',
  MISSING: 'danger',
  FOUND_ELSEWHERE: 'warning',
  DAMAGED: 'warning',
}

/** Counting what is actually there, against what the register believed. */
export function AssetStocktakePage() {
  useBreadcrumb()
  const stocktakes = useStocktakes()
  const categories = useAssetCategories()
  const entities = useLookup<{ id: string; name: string }>('legalEntities')
  const locations = useLookup<{ id: string; name: string }>('locations')
  const { openStocktake, count, closeStocktake } = useAssetMutations()

  const [selected, setSelected] = useState<string | null>(null)
  const lines = useStocktakeLines(selected ?? undefined)
  const chosen = stocktakes.data?.find((row) => row.id === selected)

  const [draft, setDraft] = useState<{
    name: string; categoryId: string; locationId: string; startedOn: string
  } | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [dialogError, setDialogError] = useState<string | null>(null)

  const act = (work: () => Promise<unknown>, fallback: string, inDialog = false) => {
    void (async () => {
      setError(null)
      setDialogError(null)
      try {
        await work()
      } catch (caught) {
        const message = toApiProblem(caught)?.detail ?? fallback
        if (inDialog) setDialogError(message)
        else setError(message)
      }
    })()
  }

  return (
    <div className="space-y-4">
      <PageHeader
        title="Stocktakes"
        subtitle="What the register believes, against what somebody found."
        actions={
          <Button
            type="button"
            onClick={() => {
              setError(null)
              setDraft({
                name: '', categoryId: '', locationId: '',
                startedOn: todayLocal(),
              })
            }}
          >
            <Plus className="size-4" aria-hidden="true" />
            New count
          </Button>
        }
      />

      {error && (
        <p className="rounded-md border border-rose-200 bg-rose-50 p-3 text-sm text-rose-800">
          {error}
        </p>
      )}

      <p className="flex items-start gap-2 rounded-md border border-slate-200 bg-slate-50 p-3 text-sm text-slate-700">
        <Info className="mt-0.5 size-4 shrink-0" aria-hidden="true" />
        Closing a count moves nothing. A missing laptop is a decision for a person — write it
        off, charge it, or look harder — and a count that silently set thirty assets to lost
        would be a report nobody trusted twice.
      </p>

      <Card>
        {stocktakes.isPending && <Skeleton className="m-4 h-20" />}
        {stocktakes.isError && (
          <ErrorState error={stocktakes.error} onRetry={() => void stocktakes.refetch()} />
        )}
        {stocktakes.isSuccess && stocktakes.data.length === 0 && (
          <EmptyState
            title="Nothing has been counted"
            description="A count writes down what the register believes, then records what was actually found beside it."
          />
        )}
        {stocktakes.isSuccess && stocktakes.data.length > 0 && (
          <div className="overflow-x-auto overscroll-x-contain">
            <table className="w-full text-sm">
              <thead className="border-b border-slate-200 text-slate-600">
                <tr>
                  <th className="p-3 text-start font-medium">Reference</th>
                  <th className="p-3 text-start font-medium">Name</th>
                  <th className="p-3 text-start font-medium">Started</th>
                  <th className="p-3 text-end font-medium">In scope</th>
                  <th className="p-3 text-end font-medium">Counted</th>
                  <th className="p-3 text-end font-medium">Missing</th>
                  <th className="p-3 text-start font-medium">Status</th>
                  <th className="p-3 text-end font-medium" />
                </tr>
              </thead>
              <tbody>
                {stocktakes.data.map((row) => (
                  <tr key={row.id} className="border-b border-slate-100">
                    <td className="p-3">
                      <button
                        type="button"
                        className="font-mono underline underline-offset-2"
                        onClick={() => setSelected(row.id)}
                      >
                        {row.reference}
                      </button>
                    </td>
                    <td className="p-3">{row.name}</td>
                    <td className="p-3"><DateValue value={row.startedOn} /></td>
                    <td className="p-3 text-end">{row.inScope}</td>
                    <td className="p-3 text-end">{row.counted}</td>
                    <td className={`p-3 text-end ${row.missing > 0 ? 'font-medium text-rose-700' : ''}`}>
                      {row.missing}
                    </td>
                    <td className="p-3">
                      <StatusBadge
                        status={row.status}
                        tone={row.status === 'CLOSED' ? 'success'
                          : row.status === 'CANCELLED' ? 'neutral' : 'info'}
                        label={row.status === 'OPEN' ? 'Counting'
                          : row.status === 'CLOSED' ? 'Closed' : 'Cancelled'}
                      />
                    </td>
                    <td className="p-3 text-end">
                      {row.status === 'OPEN' && (
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => act(
                            () => closeStocktake.mutateAsync({
                              id: row.id,
                              closedOn: todayLocal(),
                            }),
                            'That count could not be closed')}
                        >
                          Close
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {chosen && (
        <>
          <PageHeader
            title={`${chosen.reference} · ${chosen.name}`}
            subtitle={chosen.varianceNote}
          />
          <Card>
            {lines.isPending && <Skeleton className="m-4 h-20" />}
            {lines.isSuccess && lines.data.length === 0 && (
              <EmptyState
                title="Nothing was in scope"
                description="No asset matched this count's category and location."
              />
            )}
            {lines.isSuccess && lines.data.length > 0 && (
              <div className="overflow-x-auto overscroll-x-contain">
                <table className="w-full text-sm">
                  <thead className="border-b border-slate-200 text-slate-600">
                    <tr>
                      <th className="p-3 text-start font-medium">Asset</th>
                      <th className="p-3 text-start font-medium">The register said</th>
                      <th className="p-3 text-start font-medium">Found</th>
                      <th className="p-3 text-start font-medium">Note</th>
                      <th className="p-3 text-end font-medium" />
                    </tr>
                  </thead>
                  <tbody>
                    {lines.data.map((line) => (
                      <tr key={line.id} className="border-b border-slate-100 align-top">
                        <td className="p-3">
                          <span className="font-mono">{line.assetTag}</span>
                          <span className="ms-2 text-slate-600">{line.assetName}</span>
                        </td>
                        <td className="p-3 text-slate-600">
                          {ASSET_STATUS_LABELS[line.expectedStatus as AssetStatus]
                            ?? line.expectedStatus}
                          {line.expectedHolderName && ` · ${line.expectedHolderName}`}
                        </td>
                        <td className="p-3">
                          <StatusBadge
                            status={line.outcome}
                            tone={OUTCOME_TONE[line.outcome as CountOutcome] ?? 'neutral'}
                            label={COUNT_OUTCOME_LABELS[line.outcome as CountOutcome]
                              ?? line.outcome}
                          />
                        </td>
                        <td className="p-3 max-w-xs text-xs text-slate-600">{line.note ?? '—'}</td>
                        <td className="p-3 text-end">
                          {chosen.status === 'OPEN' && line.outcome === 'NOT_COUNTED' && (
                            <div className="flex justify-end gap-2">
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => act(
                                  () => count.mutateAsync({
                                    id: chosen.id,
                                    body: { assetId: line.assetId, outcome: 'FOUND' },
                                  }),
                                  'That count could not be recorded')}
                              >
                                <ClipboardList className="size-4" aria-hidden="true" />
                                Found
                              </Button>
                              <Button
                                type="button"
                                variant="outline"
                                onClick={() => act(
                                  () => count.mutateAsync({
                                    id: chosen.id,
                                    body: {
                                      assetId: line.assetId, outcome: 'MISSING',
                                      note: 'Not where the register said',
                                    },
                                  }),
                                  'That count could not be recorded')}
                              >
                                Missing
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </Card>
        </>
      )}

      <FormDialog
        open={draft != null}
        onOpenChange={(open) => { if (!open) { setDraft(null); setDialogError(null) } }}
        title="New count"
        description="Leave the scope blank to count everything in the entity."
        submitLabel="Open the count"
        loading={openStocktake.isPending}
        error={dialogError}
        onSubmit={() => {
          if (!draft) return
          act(async () => {
            await openStocktake.mutateAsync({
              legalEntityId: entities.data?.[0]?.id ?? null,
              name: draft.name,
              categoryId: draft.categoryId || null,
              locationId: draft.locationId || null,
              startedOn: draft.startedOn,
            })
            setDraft(null)
          }, 'That count could not be opened', true)
        }}
      >
        {draft && (
          <div className="grid gap-3 sm:grid-cols-2">
            <div className="sm:col-span-2">
              <Label htmlFor="stocktake-name">Name</Label>
              <Input
                id="stocktake-name" value={draft.name}
                onChange={(event) => setDraft({ ...draft, name: event.target.value })}
              />
            </div>
            <div>
              <Label htmlFor="stocktake-category">Category</Label>
              <Select
                id="stocktake-category"
                className="w-full"
                value={draft.categoryId}
                onChange={(event) => setDraft({ ...draft, categoryId: event.target.value })}
              >
                <option value="">Every category</option>
                {categories.data?.map((category) => (
                  <option key={category.id} value={category.id}>{category.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="stocktake-location">Location</Label>
              <Select
                id="stocktake-location"
                className="w-full"
                value={draft.locationId}
                onChange={(event) => setDraft({ ...draft, locationId: event.target.value })}
              >
                <option value="">Everywhere</option>
                {locations.data?.map((location) => (
                  <option key={location.id} value={location.id}>{location.name}</option>
                ))}
              </Select>
            </div>
            <div>
              <Label htmlFor="stocktake-started">Started on</Label>
              <DatePicker
                id="stocktake-started" value={draft.startedOn}
                onChange={(event) => setDraft({ ...draft, startedOn: event.target.value })}
              />
            </div>
          </div>
        )}
      </FormDialog>
    </div>
  )
}
